/**
 * Deep-link routing for notification payloads.
 *
 * Notification data comes back through external delivery surfaces, so every
 * destination is normalized and allow-listed before router.push receives it.
 */

const ALLOWED_PREFIXES = [
  '/booking/',
  '/trips/',
  '/explore/',
  '/profile/',
  '/checkin/',
  '/hosting/',
  '/messages/',
  '/services/',
  '/dispute/',
] as const

const ALLOWED_EXACT_PATHS = [
  '/profile',
  // Tabs y sub-pantallas que se usan como destino de una campaña push. Sin
  // esto, un `screen: '/explore'` (o `/hosting`, `/trips`, `/messages`) se
  // descarta y la notificación llega pero no abre nada al tocarla — el admin
  // ofrece esos destinos, así que tienen que rutear.
  '/explore',
  '/trips',
  '/hosting',
  '/messages',
] as const

/**
 * Rutas que EXISTEN pero no se pueden abrir desde afuera.
 *
 * Los pasos del wizard de publicación (`/hosting/new-listing/*`) leen todo su
 * contenido del `listing-wizard.store`: sin el wizard en curso no hay `details`,
 * ni `documents`, ni vehículo — la pantalla monta vacía y el push termina en un
 * blanco. Pasó en producción: "Cobertura preaprobada" y los avisos de
 * documentos apuntaban ahí desde cuatro funciones de la base.
 *
 * Se rechazan acá, en la única capa que sabe qué es deep-linkeable, y el
 * resolver cae al destino por `type` — que sí funciona sin estado.
 */
const NON_DEEP_LINKABLE_PREFIXES = ['/hosting/new-listing/'] as const

const TRIP_TYPES = new Set([
  'booking_confirmed',
  'booking_cancelled_by_host',
  'booking_cancelled_by_tago',
  'host_booking_cancelled',
  'host_booking_cancelled_by_tago',
  'host_new_booking',
  'host_trip_completed',
  'host_trip_started',
  'inspection_dropoff',
  'inspection_pickup',
  'inspection_assurance_accepted',
  'inspection_assurance_rejected',
  'inspection_assurance_needs_action',
  'inspection_assurance_expired',
  'inspection_assurance_failed',
  'payment_captured',
  'refund_processed',
  'rental_cancelled',
  'review_posted',
  'trip_completed',
  'trip_disputed',
  'trip_reminder',
  'trip_pickup_reminder',
  'trip_return_reminder',
  'host_booking_expiring',
  'review_reminder',
  'trip_started',
])

const VERIFICATION_TYPES = new Set([
  'document_approved',
  'document_rejected',
  'identity_approved',
  'identity_rejected',
  'license_approved',
  'license_rejected',
])

const VEHICLE_DOCUMENT_TYPES = new Set(['vehicle_document_approved', 'vehicle_document_rejected'])

const COVERAGE_REVIEW_TYPES = new Set([
  'coverage_vehicle_review_accepted',
  'coverage_vehicle_review_needs_action',
  'coverage_vehicle_review_rejected',
])

const PAYOUT_TYPES = new Set(['host_payment_captured', 'payout_completed', 'host_monthly_digest'])

const ROUTE_BASE = 'https://tago.local'
const MAX_PATH_LENGTH = 512

type NotificationData = Record<string, unknown>

function readString(data: NotificationData, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = data[key]
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed.length > 0) return trimmed
    }
  }
  return null
}

function routeWithId(prefix: string, id: string): string {
  return `${prefix}${encodeURIComponent(id)}`
}

function hasControlChars(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    if (code <= 31 || code === 127) return true
  }
  return false
}

function canonicalizeScreen(path: string): string {
  let normalized = path

  if (normalized.startsWith('/(tabs)/')) {
    normalized = normalized.replace('/(tabs)', '')
  }

  const tripReviewMatch = normalized.match(/^\/trips\/([^/?#]+)\/review(?:[?#].*)?$/)
  if (tripReviewMatch?.[1]) {
    return routeWithId('/booking/review/', decodeURIComponent(tripReviewMatch[1]))
  }

  return normalized
}

export function normalizeInternalPath(path: string | null | undefined): string | null {
  if (!path || typeof path !== 'string') return null

  const trimmed = canonicalizeScreen(path.trim())
  if (trimmed.length === 0 || trimmed.length > MAX_PATH_LENGTH) return null
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null
  if (trimmed.includes('://') || trimmed.includes('\\') || hasControlChars(trimmed)) return null

  let parsed: URL
  try {
    parsed = new URL(trimmed, ROUTE_BASE)
  } catch {
    return null
  }

  if (parsed.origin !== ROUTE_BASE) return null

  const normalized = `${parsed.pathname}${parsed.search}`
  if (NON_DEEP_LINKABLE_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
    return null
  }
  const isAllowed =
    ALLOWED_EXACT_PATHS.some((path) => parsed.pathname === path) ||
    ALLOWED_PREFIXES.some((prefix) => normalized.startsWith(prefix))
  if (!isAllowed) return null

  return normalized
}

export function isSafeInternalPath(path: string | null | undefined): path is string {
  return normalizeInternalPath(path) !== null
}

export function resolveNotificationRoute(data: unknown): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null

  const payload = data as NotificationData
  const explicitScreen = normalizeInternalPath(readString(payload, 'screen'))
  if (explicitScreen) return explicitScreen

  const type = readString(payload, 'type')
  const conversationId = readString(payload, 'conversation_id', 'conversationId')
  if (type === 'new_message' && conversationId) {
    return routeWithId('/messages/', conversationId)
  }

  const disputeId = readString(payload, 'dispute_id', 'disputeId')
  if ((type?.startsWith('dispute_') || type === 'trip_disputed') && disputeId) {
    return routeWithId('/booking/dispute/', disputeId)
  }

  const rentalId = readString(
    payload,
    'rental_id',
    'rentalId',
    'booking_id',
    'bookingId',
    'trip_id',
    'tripId',
  )
  if (type === 'payment_failed' && rentalId) {
    return `/booking/checkout?rentalId=${encodeURIComponent(rentalId)}`
  }
  if ((type ? TRIP_TYPES.has(type) : false) && rentalId) {
    return routeWithId('/trips/', rentalId)
  }

  if (type && VERIFICATION_TYPES.has(type)) {
    return '/profile/verification'
  }
  // Los pasos del wizard NO son destino de push (ver
  // NON_DEEP_LINKABLE_PREFIXES): se manda a la lista, que muestra el carro y
  // qué le falta, y desde ahí el host entra al wizard CON su estado.
  if (type && VEHICLE_DOCUMENT_TYPES.has(type)) {
    return '/hosting/listings'
  }
  if (type && COVERAGE_REVIEW_TYPES.has(type)) {
    return '/hosting/listings'
  }
  if (type && PAYOUT_TYPES.has(type)) {
    return '/hosting/payouts'
  }
  if (type === 'review_posted') {
    return '/profile'
  }
  if (type === 'new_vehicle_nearby' || type === 'vehicle_nearby') {
    const vehicleId = readString(payload, 'vehicle_id', 'vehicleId')
    if (vehicleId) {
      return routeWithId('/explore/', vehicleId)
    }
    return '/explore'
  }
  if (type === 'abandoned_checkout') {
    const rentalId = readString(payload, 'rental_id', 'rentalId')
    if (rentalId) {
      return `/booking/checkout?rentalId=${encodeURIComponent(rentalId)}`
    }
    const vehicleId = readString(payload, 'vehicle_id', 'vehicleId')
    if (vehicleId) {
      return routeWithId('/explore/', vehicleId)
    }
    return '/explore'
  }
  if (type === 'weekend_getaway' || type === 'first_trip_activation') {
    return '/explore'
  }

  return null
}

/**
 * Back-compat helper used by the inbox. It now resolves safe explicit screens
 * and deterministic type/id fallbacks.
 */
export function extractSafeScreen(data: unknown): string | null {
  return resolveNotificationRoute(data)
}

const UNPAID_HOLD_CANCEL_TYPES = new Set([
  'booking_cancelled_by_tago',
  'host_booking_cancelled_by_tago',
])

/**
 * Foreground toasts for an unpaid-hold release lie: "Reembolso completo"
 * over a trip that never existed. The inbox can still keep the row.
 * Checkout (and tours checkout) already has the honest payment toast.
 */
export function shouldShowForegroundNotificationToast(
  data: unknown,
  pathname?: string | null,
): boolean {
  if (!pathname) return true
  const onCheckout = pathname.includes('/booking/checkout') || pathname.includes('/tours/checkout')
  if (!onCheckout) return true
  if (!data || typeof data !== 'object') return true
  const type = readString(data as NotificationData, 'type')
  return !type || !UNPAID_HOLD_CANCEL_TYPES.has(type)
}
